import { FileAPIClient } from '../FileAPIClient/FileAPIClient';
import { DownloadFileTracker, FileTrackerStatusEnum } from './DownloadFileTracker';

import {
  InitMessage,
  FragmentMessage,
  FinishMessage,
  StopMessage,
  ProgressResponse,
  ErrorResponse,
} from './FileWorker';

export class Downloader {
  private apiClient: FileAPIClient;
  private changeEvent: () => void;
  private doneEvent: () => void;
  private errorEvent: (message: string) => void;
  private worker: Worker;
  hasNoNext: boolean = false;
  isCanceled: boolean = false;
  activeItem: DownloadFileTracker;

  constructor(
    changeEvent: () => void,
    doneEvent: () => void,
    errorEvent: (message: string) => void,
    apiClient: FileAPIClient,
  ) {
    this.changeEvent = changeEvent;
    this.doneEvent = doneEvent;
    this.errorEvent = errorEvent;
    this.apiClient = apiClient;
  }

  async start(fileTracker: DownloadFileTracker, symmetricKey: CryptoKey, symmetricIv: ArrayBuffer, autosave: boolean) {
    console.log('Starting download');
    this.isCanceled = false;
    this.activeItem = fileTracker;
    this.activeItem.status = FileTrackerStatusEnum.Downloading;

    const file = fileTracker.storedFile;
    if (!file) {
      this.errorEvent('/error/somethingwentwrong');
      return;
    }

    const fileFragmentInfos = await this.apiClient.getFileFragmentInfos(file.id);

    try {
      await fileTracker.initialFileFragmentNumber(fileFragmentInfos);
    } catch (error) {
      this.errorEvent('/error/resumefilewritefailedwrongsize');
      return;
    }

    this.worker = new Worker(new URL('./FileWorker.ts', import.meta.url));
    this.worker.onmessage = async (e) => {
      switch (e.data.type) {
        case 'initComplete':
          console.log('File worker ready');
          await this.handleNext();
          break;
        case 'fragmentComplete':
          console.log('File fragment done');
          await this.handleNext();
          break;
        case 'progress':
          const progressResponse: ProgressResponse = e.data;
          this.handleProgress(progressResponse);
          break;
        case 'complete':
          console.log('File done');
          await this.handleComplete();
          break;
        case 'error':
          const errorResponse: ErrorResponse = e.data;
          this.handleError(errorResponse);
          break;
        default:
          console.error('Unknown message type from worker:', e.data);
      }
    };
    this.worker.onerror = (e) => {
      const errorResponse: ErrorResponse = {
        type: 'error',
        message: '/error/somethingwentwrong',
        errorName: e.type,
        errorMessage: e.message,
      };
      this.handleError(errorResponse);
    };

    const initMessage: InitMessage = {
      type: 'init',
      fileHandle: this.activeItem.fileHandle,
      fileSize: this.activeItem.storedFile.size,
      isNewFile: this.activeItem.isNewFile,
      symmetricKey: symmetricKey,
      symmetricIv: symmetricIv,
      autosave: autosave,
    };
    console.log('Sending init message to worker', initMessage.type);
    this.worker.postMessage(initMessage);
  }

  private handleErrorStr(message: string) {
    this.errorEvent(message);
    this.isCanceled = true;
    this.activeItem.status = FileTrackerStatusEnum.Error;
    this.worker.terminate();
  }

  private handleError(errorResponse: ErrorResponse) {
    console.error('File worker reported', errorResponse.errorName, errorResponse.errorMessage);
    this.errorEvent(errorResponse.message);
    this.isCanceled = true;
    this.activeItem.status = FileTrackerStatusEnum.Error;
    this.worker.terminate();
  }

  private async handleNext() {
    if (this.isCanceled) {
      this.doneEvent();
      return;
    }

    if (this.activeItem.hasNext) {
      await this.downloadFragment();
    } else {
      this.activeItem.status = FileTrackerStatusEnum.Verifying;
      const message: FinishMessage = { type: 'finish', checksum: this.activeItem.storedFile.checksum };
      console.log('Sending finish message to worker', message.type);
      this.worker.postMessage(message);
    }
    this.changeEvent();
  }

  private async downloadFragment() {
    const fragmentVM = this.activeItem.next();
    let response;
    try {
      response = await this.apiClient.downloadFileFragment(this.activeItem.storedFile.id, fragmentVM.number);
    } catch (error) {
      console.error(`Unable to get data from API`, error.message);
      this.handleErrorStr('/error/readfiledata');
      return;
    }

    if (response.data.length === 0) {
      console.error(`File fragment number ${fragmentVM.number} has nocontent`);
      this.handleErrorStr('/error/readfiledata');
      return;
    }
    const message: FragmentMessage = { type: 'fragment', content: response.data, fragmentVM: fragmentVM };
    console.log('Sending fragment message to worker', message.type, fragmentVM.number);
    this.worker.postMessage(message);
  }

  private handleProgress(progress: ProgressResponse) {
    this.activeItem.verifyProgress = progress.progress;
    this.changeEvent();
  }

  private async handleComplete() {
    console.log('finish');
    if (this.isCanceled) {
      this.doneEvent();
      return;
    }
    const fileTracker = this.activeItem;

    const file = fileTracker.storedFile;
    await this.apiClient.fileTransferDone(file.id);
    fileTracker.storedFile.processed = new Date();

    fileTracker.downloadedFilFragmentCount = 0;
    fileTracker.status = FileTrackerStatusEnum.Done;
    fileTracker.infoMsg = ``;

    this.doneEvent();
  }

  stop() {
    if (
      this.activeItem.status === FileTrackerStatusEnum.Downloading ||
      this.activeItem.status === FileTrackerStatusEnum.Verifying
    ) {
      this.activeItem.status = FileTrackerStatusEnum.Paused;
    }
    this.isCanceled = true;
    const message: StopMessage = { type: 'stop' };
    console.log('Sending stop message to worker', message.type);
    this.worker.postMessage(message);
  }

  clear() {
    this.activeItem = null;
    this.worker.terminate();
    this.worker = null;
    console.log('Clear');
  }
}
